// Global Instructions Rule Applied!

import React, { useState } from 'react'
import { Card, Tabs } from 'antd'
import { useFundDashboard } from '../../hooks/useFundDashboard.ts'
import DashboardTab from './tabs/DashboardTab.tsx'
import InvestmentsTab from './tabs/InvestmentsTab.tsx'
import InvestorsTab from './tabs/InvestorsTab.tsx'
import TermsTab from './tabs/TermsTab.tsx'
import CapitalCallsTab from './tabs/CapitalCallsTab.tsx'
import DistributionsTab from './tabs/DistributionsTab.tsx'
import FeeBookingTab from './tabs/FeeBookingTab.tsx'
import InterestAccrualsTab from './tabs/InterestAccrualsTab.tsx'
import LedgerTab from './tabs/LedgerTab.tsx'
import ChartOfAccountsTab from './tabs/ChartOfAccountsTab.tsx'
import StatementsTab from './tabs/StatementsTab.tsx'
import { ChangeLogTab } from '../../../change_log/components/ChangeLogTab.tsx'
import NewFundForm from './NewFundForm.tsx'

interface FullFundFormProps {
  recordId?: string | null
  isNew?: boolean
}

/**
 * Funds full form. `/funds/forms/full/new` renders the New Fund form
 * (Details / Status / Key Dates / Fundraising / Manager + Create); once
 * created the tab morphs into `/funds/forms/full/:id` and the tabbed form
 * below takes over.
 */
export default function FullFundForm({ recordId, isNew }: FullFundFormProps) {
  if (isNew || !recordId) return <NewFundForm />
  return <FundFormTabs recordId={recordId} />
}

function FundFormTabs({ recordId }: { recordId: string }) {
  const isNew = false
  const [activeTab, setActiveTab] = useState<string>('dashboard')
  const { detail, loading } = useFundDashboard(recordId)

  return (
    <div className='h-full w-full min-h-0 flex flex-col bg-gray-50 p-3 overflow-hidden'>
      <Card size='small' className='flex-1 min-h-0 flex flex-col h-full' classNames={{ body: 'h-full p-0' }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          type='line'
          size='middle'
          className='deal-form-tabs h-full'
          items={[
            {
              key: 'dashboard',
              label: 'Dashboard',
              children: (
                <div className='h-full'>
                  <DashboardTab
                    detail={detail}
                    detailLoading={loading}
                    fundId={isNew ? null : recordId ?? null}
                  />
                </div>
              )
            },
            {
              key: 'investments',
              label: 'Investments',
              children: (
                <div className='h-full'>
                  <InvestmentsTab fundId={isNew ? null : recordId ?? null} detail={detail} />
                </div>
              )
            },
            {
              key: 'investors',
              label: 'Investors',
              children: (
                <div className='h-full'>
                  <InvestorsTab fundId={isNew ? null : recordId ?? null} />
                </div>
              )
            },
            {
              key: 'terms',
              label: 'Terms',
              children: (
                <div className='h-full'>
                  <TermsTab fundId={isNew ? null : recordId ?? null} />
                </div>
              )
            },
            {
              key: 'capital-calls',
              label: 'Capital Calls',
              children: (
                <div className='h-full'>
                  <CapitalCallsTab fundId={isNew ? null : recordId ?? null} />
                </div>
              )
            },
            {
              key: 'distributions',
              label: 'Distributions',
              children: (
                <div className='h-full'>
                  <DistributionsTab fundId={isNew ? null : recordId ?? null} />
                </div>
              )
            },
            {
              key: 'fee-booking',
              label: 'Fee Booking',
              children: (
                <div className='h-full'>
                  <FeeBookingTab fundId={isNew ? null : recordId ?? null} />
                </div>
              )
            },
            {
              key: 'interest-accruals',
              label: 'Interest Accruals',
              children: (
                <div className='h-full'>
                  <InterestAccrualsTab fundId={isNew ? null : recordId ?? null} />
                </div>
              )
            },
            {
              key: 'ledger',
              label: 'Ledger',
              children: (
                <div className='h-full'>
                  <LedgerTab fundId={isNew ? null : recordId ?? null} />
                </div>
              )
            },
            {
              key: 'chart-of-accounts',
              label: 'Chart of Accounts',
              children: (
                <div className='h-full'>
                  <ChartOfAccountsTab fundId={isNew ? null : recordId ?? null} />
                </div>
              )
            },
            {
              key: 'statements',
              label: 'Statements',
              children: (
                <div className='h-full'>
                  <StatementsTab fundId={isNew ? null : recordId ?? null} />
                </div>
              )
            },
            ...(!isNew && recordId
              ? [
                  {
                    key: 'change-log',
                    label: 'Change Log',
                    children: (
                      <div data-debug='full-form-change-log-tab' className='h-full'>
                        <ChangeLogTab key={`change-log-${recordId}`} contextTable='funds' recordId={recordId} />
                      </div>
                    )
                  }
                ]
              : [])
          ]}
        />
      </Card>
    </div>
  )
}
